import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Camera, Aperture, Timer, Calendar } from "lucide-react";
import { api, photoSrc } from "@/lib/api";

const filters = [
  { id: "all", label: "Tout" },
  { id: "plats", label: "Plats" },
  { id: "restaurants", label: "Restaurants" },
  { id: "produits", label: "Produits" },
  { id: "editorial", label: "Éditorial" },
];

const spanClass = (span) => {
  if (span === "wide") return "lg:col-span-8 aspect-[16/10]";
  if (span === "tall") return "lg:col-span-4 lg:row-span-2 aspect-[3/4] lg:aspect-auto";
  return "lg:col-span-4 aspect-[4/3]";
};

export const Portfolio = () => {
  const [active, setActive] = useState("all");
  const [photos, setPhotos] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/photos${active === "all" ? "" : `?category=${active}`}`);
    setPhotos(data);
  };
  useEffect(() => { load(); }, [active]);

  return (
    <section id="portfolio" className="py-24 lg:py-32 bg-[#141412]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div>
            <div className="overline mb-4">Portfolio sélectionné</div>
            <h2 className="font-serif-d text-4xl lg:text-6xl tracking-tight">Une table, mille histoires.</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {filters.map((f) => (
              <button key={f.id} data-testid={`portfolio-filter-${f.id === "all" ? "all" : f.id}`} onClick={() => setActive(f.id)}
                className={`relative font-sub text-sm px-5 py-2.5 rounded-full transition-colors ${active === f.id ? "text-[#0D0D0C]" : "text-[#A39E93] hover:text-[#F4F0EB]"}`}>
                {active === f.id && <motion.span layoutId="filter-pill" className="absolute inset-0 bg-[#C88D51] rounded-full" transition={{ type: "spring", stiffness: 400, damping: 35 }} />}
                <span className="relative z-10">{f.label}</span>
              </button>
            ))}
          </div>
        </div>

        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 auto-rows-auto">
          <AnimatePresence mode="popLayout">
            {photos.map((p, i) => (
              <motion.button key={p.id} layout data-testid={`gallery-item-card-${i}`}
                initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.5, delay: i * 0.04 }}
                onClick={() => setSelected(p)}
                className={`group relative overflow-hidden border border-[#F4F0EB]/8 ${spanClass(p.span)}`}>
                <img src={photoSrc(p)} alt={p.title} className="card-img w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0C]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 p-5 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 text-left">
                  <div className="font-mono-a text-[0.6rem] uppercase tracking-[0.2em] text-[#C88D51]">{p.category}</div>
                  <div className="font-serif-d text-xl">{p.title}</div>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#0D0D0C]/95 backdrop-blur-md flex items-center justify-center p-4 lg:p-10" onClick={() => setSelected(null)}>
            <button data-testid="lightbox-close-button" onClick={() => setSelected(null)} className="absolute top-6 right-6 text-[#F4F0EB] hover:text-[#C88D51] transition-colors"><X className="w-7 h-7" /></button>
            <motion.div initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.94, opacity: 0 }}
              className="max-w-6xl w-full grid lg:grid-cols-3 gap-6 items-center" onClick={(e) => e.stopPropagation()}>
              <img src={photoSrc(selected)} alt={selected.title} className="lg:col-span-2 w-full max-h-[80vh] object-contain border border-[#F4F0EB]/10" />
              <div className="lg:col-span-1">
                <div className="overline mb-3">{selected.category}</div>
                <h3 className="font-serif-d text-3xl mb-6">{selected.title}</h3>
                <div className="space-y-3 font-mono-a text-xs text-[#A39E93]">
                  <div className="flex items-center gap-3"><Camera className="w-4 h-4 text-[#C88D51]" /> {selected.camera}</div>
                  <div className="flex items-center gap-3"><Aperture className="w-4 h-4 text-[#C88D51]" /> {selected.aperture}</div>
                  <div className="flex items-center gap-3"><Timer className="w-4 h-4 text-[#C88D51]" /> {selected.shutter} · ISO {selected.iso}</div>
                  <div className="flex items-center gap-3"><Calendar className="w-4 h-4 text-[#C88D51]" /> {selected.year}</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
